import React from 'react';
import { entityStandaloneRegistry, StandaloneRendererContext } from '../components/StandaloneEntity/entityStandaloneRegistry';
import { lazyWithReload } from '../utils/lazyWithReload';

const NotesApp_v2 = lazyWithReload(() =>
  import('./NotesApp_v2/NotesApp_v2').then(m => ({ default: m.NotesApp_v2 }))
);
const CanvasApp_v2 = lazyWithReload(() =>
  import('./CanvasApp_v2/CanvasApp_v2').then(m => ({ default: m.CanvasApp_v2 }))
);
const PageApp_v2 = lazyWithReload(() =>
  import('./PageApp_v2/PageApp_v2').then(m => ({ default: m.PageApp_v2 }))
);
const PresentationsApp_v2 = lazyWithReload(() =>
  import('./PresentationsApp_v2/PresentationsApp_v2').then(m => ({ default: m.PresentationsApp_v2 }))
);
const DashboardApp = lazyWithReload(() => import('./DashboardApp/DashboardApp'));
const TasksApp = lazyWithReload(() => import('./TasksApp/TasksApp'));
const ContactsApp = lazyWithReload(() =>
  import('./ContactsApp/ContactsApp').then(m => ({ default: m.ContactsApp }))
);
const BookFormatterApp_v2 = lazyWithReload(() =>
  import('./BookFormatterApp_v2/BookFormatterApp_v2').then(m => ({ default: m.BookFormatterApp_v2 }))
);
const VideoStudioApp = lazyWithReload(() => import('./VideoStudioApp/VideoStudioApp'));
const DatabaseApp = lazyWithReload(() => import('./DatabaseApp/DatabaseApp'));
const DiagramApp = lazyWithReload(() => import('./DiagramApp/DiagramApp'));
const RulesApp_v2 = lazyWithReload(() => import('./RulesApp_v2/RulesApp_v2'));
const WebDocumentApp = lazyWithReload(() => import('./WebDocumentApp/WebDocumentApp'));
const FileEditorApp = lazyWithReload(() => import('./FileEditorApp/FileEditorApp'));
const MemoryApp_v2 = lazyWithReload(() => import('./MemoryApp_v2/MemoryApp_v2'));
const PromptsApp_v2 = lazyWithReload(() => import('./PromptsApp_v2/PromptsApp_v2'));
const SkillsApp_v2 = lazyWithReload(() => import('./SkillsApp_v2/SkillsApp_v2'));
const SkillWizardApp = lazyWithReload(() => import('./SkillWizardApp/SkillWizardApp'));
const HtmlCanvasApp = lazyWithReload(() => import('./HtmlCanvasApp/HtmlCanvasApp'));

function entityId(ctx: StandaloneRendererContext): string {
  return ctx.entity._id || (ctx.entity as any).id || '';
}

entityStandaloneRegistry.register('Note', {
  appId: 'notes-v2',
  render: (ctx) => React.createElement(NotesApp_v2, { entityId: entityId(ctx) }),
});

entityStandaloneRegistry.register('Content.Note', {
  appId: 'notes-v2',
  render: (ctx) => React.createElement(NotesApp_v2, { entityId: entityId(ctx) }),
});

entityStandaloneRegistry.register('Page', {
  appId: 'page-v2',
  render: (ctx) => React.createElement(PageApp_v2, { entityId: entityId(ctx) }),
});

entityStandaloneRegistry.register('Canvas', {
  appId: 'canvas-v2',
  render: (ctx) => React.createElement(CanvasApp_v2, { entityId: entityId(ctx) }),
});

const presentationRenderer = {
  appId: 'presentations-v2',
  render: (ctx: StandaloneRendererContext) => React.createElement(PresentationsApp_v2, {
    entityId: entityId(ctx),
    windowId: `standalone-presentation-${entityId(ctx)}`,
  }),
};
entityStandaloneRegistry.register('Presentation', presentationRenderer);
entityStandaloneRegistry.register('Slides', presentationRenderer);

entityStandaloneRegistry.register('Dashboard', {
  appId: 'dashboard',
  render: (ctx) => React.createElement(DashboardApp, {
    entityId: entityId(ctx),
    entity: ctx.entity,
    spaceId: (ctx.entity as any).spaceId || 'default',
    windowId: `standalone-dashboard-${entityId(ctx)}`,
  }),
});

const taskRenderer = {
  appId: 'tasks',
  render: (ctx: StandaloneRendererContext) => React.createElement(TasksApp, {
    windowId: `standalone-task-${entityId(ctx)}`,
    spaceId: (ctx.entity as any).spaceId || 'default',
    entity: ctx.entity,
    renderMode: 'standalone',
    title: ctx.entity.name || 'Tasks',
  }),
};
entityStandaloneRegistry.register('Task', taskRenderer);
entityStandaloneRegistry.register('Project', taskRenderer);
entityStandaloneRegistry.register('Area', taskRenderer);

const contactRenderer = {
  appId: 'contacts',
  render: (ctx: StandaloneRendererContext) => React.createElement(ContactsApp, {
    windowId: `standalone-contact-${entityId(ctx)}`,
    spaceId: (ctx.entity as any).spaceId || 'default',
    entity: ctx.entity,
  }),
};
entityStandaloneRegistry.register('Contact', contactRenderer);
entityStandaloneRegistry.register('ContactGroup', contactRenderer);

entityStandaloneRegistry.register('BookProject', {
  appId: 'book_formatter_v2',
  render: (ctx) => React.createElement(BookFormatterApp_v2, {
    entityId: entityId(ctx),
    windowId: `standalone-book-${entityId(ctx)}`,
    spaceId: (ctx.entity as any).spaceId || 'default',
  }),
});

entityStandaloneRegistry.register('VideoProject', {
  appId: 'videostudio',
  render: (ctx) => React.createElement(VideoStudioApp, {
    windowId: `standalone-video-${entityId(ctx)}`,
    spaceId: (ctx.entity as any).spaceId || 'default',
    entity: ctx.entity,
    renderMode: 'standalone',
    title: ctx.entity.name || 'Video',
  }),
});

entityStandaloneRegistry.register('Table', {
  appId: 'database',
  render: (ctx) => React.createElement(DatabaseApp, {
    windowId: `standalone-table-${entityId(ctx)}`,
    spaceId: (ctx.entity as any).spaceId || 'default',
    entity: ctx.entity,
    renderMode: 'standalone',
    title: ctx.entity.name || 'Table',
  }),
});

entityStandaloneRegistry.register('Diagram', {
  appId: 'diagram',
  render: (ctx) => React.createElement(DiagramApp, {
    windowId: `standalone-diagram-${entityId(ctx)}`,
    spaceId: (ctx.entity as any).spaceId || 'default',
    entityId: entityId(ctx),
    entity: ctx.entity,
    renderMode: 'standalone',
    title: ctx.entity.name || 'Diagram',
  }),
});

entityStandaloneRegistry.register('Rule', {
  appId: 'rules',
  render: (ctx) => React.createElement(RulesApp_v2, {
    windowId: `standalone-rule-${entityId(ctx)}`,
    spaceId: (ctx.entity as any).spaceId || 'default',
    entityId: entityId(ctx),
  }),
});

const webRenderer = {
  appId: 'webdocument',
  render: (ctx: StandaloneRendererContext) => React.createElement(WebDocumentApp, {
    entityId: entityId(ctx),
    title: ctx.entity.name || 'Web Link',
  }),
};
entityStandaloneRegistry.register('WebDocument', webRenderer);
entityStandaloneRegistry.register('Content.WebDocument', webRenderer);

entityStandaloneRegistry.register('File', {
  appId: 'file-editor',
  render: (ctx) => React.createElement(FileEditorApp, {
    windowId: `standalone-file-${entityId(ctx)}`,
    spaceId: (ctx.entity as any).spaceId || 'default',
    entityId: entityId(ctx),
  }),
});


entityStandaloneRegistry.register('Memory', {
  appId: 'memory-v2',
  render: (ctx) => React.createElement(MemoryApp_v2, {
    windowId: `standalone-memory-${entityId(ctx)}`,
    spaceId: (ctx.entity as any).spaceId || 'default',
    entityId: entityId(ctx),
  }),
});

entityStandaloneRegistry.register('Prompt', {
  appId: 'prompts-v2',
  render: (ctx) => React.createElement(PromptsApp_v2, {
    windowId: `standalone-prompt-${entityId(ctx)}`,
    spaceId: (ctx.entity as any).spaceId || 'default',
    entityId: entityId(ctx),
  }),
});

entityStandaloneRegistry.register('Skill', {
  appId: 'skills-v2',
  render: (ctx) => React.createElement(SkillsApp_v2, {
    windowId: `standalone-skill-${entityId(ctx)}`,
    spaceId: (ctx.entity as any).spaceId || 'default',
    entityId: entityId(ctx),
  }),
});

entityStandaloneRegistry.register('SkillWizard', {
  appId: 'skill-wizard',
  render: (ctx) => React.createElement(SkillWizardApp, {
    windowId: `standalone-skillwizard-${entityId(ctx)}`,
    spaceId: (ctx.entity as any).spaceId || 'default',
    entity: ctx.entity,
    entityId: entityId(ctx),
    renderMode: 'standalone',
    title: ctx.entity.name || 'Skill Wizard',
  }),
});

entityStandaloneRegistry.register('HtmlCanvas', {
  appId: 'html-canvas',
  render: (ctx) => React.createElement(HtmlCanvasApp, {
    windowId: `standalone-htmlcanvas-${entityId(ctx)}`,
    spaceId: (ctx.entity as any).spaceId || 'default',
    entity: ctx.entity,
    entityId: entityId(ctx),
    renderMode: 'standalone',
    title: ctx.entity.name || 'HTML Canvas',
  }),
});
